import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Check, Crown, Star, Shield, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PremiumFeaturesPage = () => {
  const { t } = useLanguage();
  const { token, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const premiumFeatures = [
    {
      id: 'identity_verification',
      icon: Shield,
      title: t('premium.verification.title') || 'Identity Verification',
      description: t('premium.verification.desc') || 'Get a verified badge on your profile to build trust with hosts and guests.',
      price: '€9.99',
      priceType: 'one-time',
      benefits: [
        t('premium.verification.benefit1') || 'Verified badge on profile',
        t('premium.verification.benefit2') || 'Higher trust score',
        t('premium.verification.benefit3') || 'Priority in applications',
        t('premium.verification.benefit4') || 'Lifetime verification',
      ],
      color: 'from-blue-500 to-cyan-500',
      forRoles: ['host', 'guest'],
    },
    {
      id: 'featured_listing',
      icon: Star,
      title: t('premium.featured.title') || 'Featured Listing',
      description: t('premium.featured.desc') || 'Make your wedding stand out and get more applications.',
      price: '€29',
      priceType: '30 days',
      benefits: [
        t('premium.featured.benefit1') || 'Highlighted on homepage',
        t('premium.featured.benefit2') || 'Top of search results',
        t('premium.featured.benefit3') || 'Featured badge',
        t('premium.featured.benefit4') || '30 days visibility boost',
      ],
      color: 'from-amber-500 to-orange-500',
      forRoles: ['host'],
    },
    {
      id: 'premium_account',
      icon: Crown,
      title: t('premium.account.title') || 'Premium Account',
      description: t('premium.account.desc') || 'Unlock exclusive features and become a VIP guest.',
      price: '€14.99',
      priceType: 'per month',
      benefits: [
        t('premium.account.benefit1') || 'Unlimited applications',
        t('premium.account.benefit2') || 'See who viewed your profile',
        t('premium.account.benefit3') || 'Priority customer support',
        t('premium.account.benefit4') || 'Early access to new weddings',
      ],
      color: 'from-purple-500 to-pink-500',
      forRoles: ['guest'],
    },
  ];

  const handlePurchase = async (featureId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(featureId);

    try {
      const response = await axios.post(
        `${API_URL}/api/premium/purchase`,
        {
          feature_type: featureId,
          origin_url: window.location.origin,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start purchase');
    } finally {
      setLoading(null);
    }
  };

  const canPurchase = (feature) => {
    if (!user) return true;
    return feature.forRoles.includes(user.role);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-primary/10 via-background to-background overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-5"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <Badge className="mb-4 bg-primary/10 text-primary">
              <Sparkles className="h-3 w-3 mr-1" />
              {t('premium.badge') || 'Premium Features'}
            </Badge>
            <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6">
              {t('premium.title') || 'Unlock Your Full Potential'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('premium.subtitle') || 'Upgrade your experience with premium features designed to help you connect better and stand out.'}
            </p>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {premiumFeatures.map((feature) => (
                <Card 
                  key={feature.id} 
                  className={`relative overflow-hidden transition-all hover:shadow-xl ${
                    !canPurchase(feature) ? 'opacity-50' : ''
                  }`}
                >
                  {/* Gradient top bar */}
                  <div className={`h-2 bg-gradient-to-r ${feature.color}`}></div>
                  
                  <CardHeader className="text-center pb-2">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center`}>
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="text-center">
                    <div className="mb-6">
                      <span className="text-4xl font-bold">{feature.price}</span>
                      <span className="text-muted-foreground ml-1">/ {feature.priceType}</span>
                    </div>
                    
                    <ul className="space-y-3 text-left">
                      {feature.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  
                  <CardFooter>
                    <Button 
                      className={`w-full bg-gradient-to-r ${feature.color} hover:opacity-90`}
                      onClick={() => handlePurchase(feature.id)}
                      disabled={loading === feature.id || !canPurchase(feature)}
                    >
                      {loading === feature.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('common.loading') || 'Loading...'}
                        </>
                      ) : !canPurchase(feature) ? (
                        t('premium.notAvailable') || 'Not available for your role'
                      ) : (
                        t('premium.purchase') || 'Purchase Now'
                      )}
                    </Button>
                  </CardFooter>
                  
                  {/* Role badge */}
                  <div className="absolute top-4 right-4">
                    <Badge variant="outline" className="text-xs">
                      {feature.forRoles.includes('host') && feature.forRoles.includes('guest') 
                        ? t('premium.forAll') || 'For Everyone'
                        : feature.forRoles.includes('host') 
                          ? t('premium.forHosts') || 'For Hosts'
                          : t('premium.forGuests') || 'For Guests'
                      }
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-secondary">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-3xl font-bold text-center mb-12">
              {t('premium.faq.title') || 'Frequently Asked Questions'}
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-2">{t('premium.faq.q1') || 'How does payment work?'}</h3>
                <p className="text-muted-foreground">{t('premium.faq.a1') || 'All payments are processed securely through Stripe. Your payment information is never stored on our servers.'}</p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-2">{t('premium.faq.q2') || 'Can I get a refund?'}</h3>
                <p className="text-muted-foreground">{t('premium.faq.a2') || 'We offer a 7-day money-back guarantee for premium accounts. Featured listings are non-refundable once activated.'}</p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-2">{t('premium.faq.q3') || 'How long does verification take?'}</h3>
                <p className="text-muted-foreground">{t('premium.faq.a3') || 'Identity verification is instant after successful payment. Your badge will appear on your profile immediately.'}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default PremiumFeaturesPage;
